import React from "react";
import { CheckCircle2, XCircle, Ban, Radio, ArrowRight } from "lucide-react";
import { inr, timeFrom } from "../lib/format";

const STATUS = {
  matched: { label: "Matched", icon: Radio, cls: "text-sky-300 ring-sky-400/40 bg-sky-500/10" },
  pending: { label: "Matched", icon: Radio, cls: "text-sky-300 ring-sky-400/40 bg-sky-500/10" },
  open: { label: "Open", icon: Radio, cls: "text-sky-300 ring-sky-400/40 bg-sky-500/10" },
  won: { label: "Won", icon: CheckCircle2, cls: "text-emerald-300 ring-emerald-400/40 bg-emerald-500/10" },
  lost: { label: "Lost", icon: XCircle, cls: "text-red-300 ring-red-400/40 bg-red-500/10" },
  void: { label: "Void · Refunded", icon: Ban, cls: "text-amber-300 ring-amber-400/40 bg-amber-500/10" },
  cancelled: { label: "Cancelled", icon: Ban, cls: "text-zinc-300 ring-zinc-400/30 bg-zinc-500/10" },
};

const ACCENT = {
  back: "from-[#72BBEF] to-[#3b8fd6]",
  lay: "from-[#F994BA] to-[#e0558f]",
  yes: "from-emerald-400 to-emerald-600",
  no: "from-rose-400 to-rose-600",
  toss: "from-sky-400 to-indigo-500",
  casino: "from-[#f5b400] to-[#c98a00]",
};

export default function BetCard({ b, compact = false }) {
  const fs = compact ? { ref: "text-[8px]", sub: "text-[8px]", badge: "text-[8px] px-1.5 py-[1px]", title: "text-[11px]", odds: "text-[11px]", tag: "text-[7px] px-1", amt: "text-[10px]", meta: "text-[8px]", pad: "px-2.5 py-1.5" }
    : { ref: "text-[10px]", sub: "text-[10px]", badge: "text-[10px] px-2 py-[2px]", title: "text-[14px]", odds: "text-[14px]", tag: "text-[9px] px-1.5", amt: "text-[13px]", meta: "text-[10px]", pad: "px-3 py-2.5" };
  const s = STATUS[b.status] || STATUS.matched;
  const lost = b.status === "lost";
  const won = b.status === "won";
  const voided = b.status === "void" || b.status === "cancelled";
  const isCasino = b.kind === "casino";
  const isSession = b.kind === "session" || isCasino;
  const accentKey = isCasino ? "casino" : isSession ? (b.isToss ? "toss" : b.side) : (b.side === "lay" ? "lay" : "back");
  const stake = isSession ? b.stake : (b.risk ?? b.stake);
  const ret = voided ? stake : b.potentialReturn;
  const retLabel = won ? "Payout" : lost ? "Lost" : voided ? "Refunded" : "Returns";
  const tag = isCasino
    ? b.tag
    : isSession
      ? (b.isToss ? "Toss" : `${b.side === "yes" ? "Yes" : "No"} ${b.line}`)
      : (b.side === "lay" ? "Khai" : "Lagai");
  const title = isSession ? b.sessionName : b.selectionLabel;
  const sub = isSession
    ? `${b.matchLabel}${b.isToss && b.tossWinner ? ` · won by ${b.tossWinner}` : b.resultRuns != null ? ` · result ${b.resultRuns} runs` : ""}`
    : `${b.matchLabel} · ${b.marketName}`;

  return (
    <div
      data-testid={isCasino ? `casino-bet-card-${b.id}` : isSession ? `session-bet-card-${b.id}` : `bet-card-${b.id}`}
      className={`group relative flex overflow-hidden rounded-xl bg-[#111318] ring-1 ring-white/[0.07] shadow-[0_8px_24px_-16px_rgba(0,0,0,0.9)] transition-[transform,box-shadow,opacity] hover:-translate-y-px hover:ring-white/15 ${lost ? "opacity-70" : ""}`}
    >
      <span className={`w-1 shrink-0 bg-gradient-to-b ${ACCENT[accentKey] || ACCENT.back}`} />
      <div className={`flex-1 min-w-0 ${fs.pad}`}>
        <div className="flex items-center gap-2 min-w-0">
          <span data-testid={isSession ? undefined : `bet-ref-${b.id}`} className={`font-mono ${fs.ref} font-bold text-[#f5b400]/90 tracking-wider shrink-0`}>{b.ref || "—"}</span>
          <span className={`${fs.sub} text-zinc-500 truncate`}>{sub}</span>
          <span className={`ml-auto inline-flex items-center gap-1 rounded-full ${fs.badge} font-bold ring-1 shrink-0 ${s.cls}`}>
            <s.icon className="h-3 w-3" /> {s.label}
          </span>
        </div>
        <div className="mt-1 flex items-center justify-between gap-3">
          <div className="min-w-0 flex items-center gap-2">
            <span className={`font-display font-bold ${fs.title} text-white truncate`}>{title}</span>
            {!isSession && <span className={`font-display font-bold ${fs.odds} text-[#f5b400] tabular-nums shrink-0`}>@{b.odds != null ? b.odds.toFixed(2) : "—"}</span>}
            <span className={`shrink-0 ${fs.tag} font-extrabold uppercase tracking-wide rounded py-[2px] text-black bg-gradient-to-r ${ACCENT[accentKey] || ACCENT.back}`}>{tag}</span>
          </div>
          <div className={`flex items-center gap-1.5 shrink-0 ${fs.amt} tabular-nums`}>
            <span className="text-zinc-300 font-semibold">{inr(stake)}</span>
            <ArrowRight className="h-3 w-3 text-zinc-600" />
            <span className={`font-display font-bold ${lost ? "text-red-400 line-through decoration-red-400/70" : won ? "text-emerald-400" : voided ? "text-amber-300" : "text-white"}`} title={retLabel}>{inr(ret)}</span>
          </div>
        </div>
        <div className={`mt-0.5 flex items-center justify-between ${fs.meta} text-zinc-500`}>
          <span>{timeFrom(b.placedAt)}</span>
          <span>{b.side === "lay" && !isSession ? "Liability" : "Stake"} → {retLabel}</span>
        </div>
      </div>
    </div>
  );
}
